import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ChatBubbleLeftRightIcon, 
  XMarkIcon, 
  PaperAirplaneIcon,
  UserIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { apiClient } from '../../services/api';
import toast from 'react-hot-toast';

const SupportChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [humanSupportAvailable, setHumanSupportAvailable] = useState(false);
  const [showHumanSupportBanner, setShowHumanSupportBanner] = useState(true);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages when human support is active
  useEffect(() => {
    if (!conversation?.human_support_requested) return;

    const pollInterval = setInterval(async () => {
      try {
        const messagesResponse = await apiClient.get(`/support-chat/conversation/${conversation.id}/messages`);
        const newMessages = messagesResponse.data.messages || [];

        // Only update if there are new messages
        if (newMessages.length !== messages.length) {
          setMessages(newMessages);
        }
      } catch (error) {
        console.error('Failed to poll messages:', error);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [conversation?.human_support_requested, conversation?.id, messages.length]);

  const initializeChat = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Get or create conversation
      const conversationResponse = await apiClient.get('/support-chat/conversation');
      const conv = conversationResponse.data.conversation;
      setConversation(conv);

      // Load message history
      const messagesResponse = await apiClient.get(`/support-chat/conversation/${conv.id}/messages`);
      setMessages(messagesResponse.data.messages || []);

      // If no messages, send welcome message
      if (messagesResponse.data.messages.length === 0) {
        await apiClient.post(`/support-chat/conversation/${conv.id}/messages`, {
          message: 'Hello! I need help with the invoice system.'
        });
        const refreshedMessages = await apiClient.get(`/support-chat/conversation/${conv.id}/messages`);
        setMessages(refreshedMessages.data.messages || []);
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      toast.error('Failed to initialize chat');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkHumanSupportAvailability = useCallback(async () => {
    try {
      const response = await apiClient.get('/support-chat/human-support/availability');
      setHumanSupportAvailable(response.data.isAvailable);
    } catch (error) {
      console.error('Failed to check human support availability:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !conversation) {
      initializeChat();
    }
  }, [conversation, initializeChat, isOpen]);

  useEffect(() => {
    checkHumanSupportAvailability();
  }, [checkHumanSupportAvailability]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversation || isLoading) return;

    const messageText = newMessage.trim();
    const tempUserMessage = {
      id: Date.now(), // Temporary ID
      conversation_id: conversation.id,
      sender_type: 'user',
      sender_id: null,
      message_text: messageText,
      created_at: new Date().toISOString(),
      is_system_message: false
    };

    // Immediately add user message to UI
    setMessages(prev => [...prev, tempUserMessage]);
    setNewMessage('');
    setIsTyping(true);

    try {
      const response = await apiClient.post(`/support-chat/conversation/${conversation.id}/messages`, {
        message: messageText
      });

      // Reload messages to get the complete conversation with proper IDs and AI response
      const messagesResponse = await apiClient.get(`/support-chat/conversation/${conversation.id}/messages`);
      setMessages(messagesResponse.data.messages || []);

      // Update conversation state if human support was requested
      if (response.data.humanSupportRequested) {
        setConversation(prev => ({ ...prev, human_support_requested: true }));
      }

      if (!response.data.success) {
        toast.error(response.data.message || 'Failed to send message');
        // Remove the temporary message on error
        setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      // Remove the temporary message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
    } finally {
      setIsTyping(false);
    }
  };

  const requestHumanSupport = async () => {
    if (!conversation) return;

    try {
      const response = await apiClient.post(`/support-chat/conversation/${conversation.id}/request-human`);

      if (response.data.success) {
        toast.success(response.data.message);

        // Update conversation state and show banner
        setConversation(prev => ({ ...prev, human_support_requested: true }));
        setShowHumanSupportBanner(true);

        // Reload messages to show the system message
        const messagesResponse = await apiClient.get(`/support-chat/conversation/${conversation.id}/messages`);
        setMessages(messagesResponse.data.messages || []);
      } else {
        toast.error(response.data.message || 'Failed to request human support');
      }
    } catch (error) {
      console.error('Failed to request human support:', error);
      toast.error('Failed to request human support');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSenderIcon = (senderType) => {
    switch (senderType) {
      case 'user':
        return <UserIcon className="h-5 w-5" />;
      case 'ai':
        return <ComputerDesktopIcon className="h-5 w-5" />;
      case 'human_agent':
        return <UserIcon className="h-5 w-5 text-green-600" />;
      default:
        return <ComputerDesktopIcon className="h-5 w-5" />;
    }
  };

  const getSenderName = (senderType, senderName) => {
    switch (senderType) {
      case 'user':
        return 'You';
      case 'ai':
        return 'AI Assistant';
      case 'human_agent':
        return senderName || 'Support Agent';
      default:
        return 'System';
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-colors duration-200 flex items-center space-x-2"
        >
          <ChatBubbleLeftRightIcon className="h-6 w-6" />
          <span className="hidden sm:inline">Support Chat</span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 h-[600px] bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          <span className="font-medium">Support Chat</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-white hover:text-gray-200 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${
                    message.sender_type === 'user'
                      ? 'bg-blue-600 text-white'
                      : message.is_system_message
                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    {getSenderIcon(message.sender_type)}
                    <span className="text-xs font-medium">
                      {getSenderName(message.sender_type, message.sender_name)}
                    </span>
                    <span className="text-xs opacity-75">
                      {formatTime(message.created_at)}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{message.message_text}</p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-900 max-w-xs lg:max-w-md px-3 py-2 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <ComputerDesktopIcon className="h-4 w-4" />
                    <span className="text-xs">AI Assistant is typing...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Human Support Banner (if requested and banner is shown) */}
      {conversation?.human_support_requested && showHumanSupportBanner && (
        <div className="border-t border-gray-200 bg-blue-50 border-b border-blue-200">
          <div className="p-3 relative">
            <button
              onClick={() => setShowHumanSupportBanner(false)}
              className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
            <div className="pr-6">
              <div className="flex items-center space-x-2 mb-1">
                <UserIcon className="h-4 w-4 text-blue-600" />
                <h3 className="text-xs font-medium text-blue-900">
                  Human Support Requested
                </h3>
              </div>
              <p className="text-xs text-blue-700">
                {conversation.status === 'human_assigned'
                  ? 'A human agent has been assigned and will respond shortly.'
                  : 'Your request has been received. A human agent will assist you during business hours (9:00 AM - 5:00 PM PKT).'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex space-x-2 mb-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={conversation?.human_support_requested
              ? "Type your message for the human agent..."
              : "Type your message..."}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading || isTyping}
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || isLoading || isTyping}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white p-2 rounded-md transition-colors"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Human Support Button (only show if not requested) */}
        {!conversation?.human_support_requested && (
          <button
            onClick={requestHumanSupport}
            disabled={!conversation}
            className={`w-full text-xs py-2 px-3 rounded-md transition-colors flex items-center justify-center space-x-1 ${
              humanSupportAvailable
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            {humanSupportAvailable ? (
              <>
                <UserIcon className="h-4 w-4" />
                <span>Request Human Support (Available Now)</span>
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>Request Human Support (9AM-5PM PKT)</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SupportChat;
